import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LinkText from './LinkText';
import ConnectRanksButton from './ConnectRanksButton';
import {
    DndContext,
    closestCorners,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableItem({
    id,
    rank,
    children,
    isVetoed = false,
    onVeto,
    onUnVeto,
    isConnectedToNext = false,
    isConnectedFromPrevious = false,
    t
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: sortableIsDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: sortableIsDragging ? 0.8 : 1,
    };

    const handleButtonClick = (e) => {
        e.stopPropagation();
        if (isVetoed && onUnVeto) {
            onUnVeto(id);
        } else if (!isVetoed && onVeto) {
            onVeto(id);
        }
    };

    const handleButtonMouseDown = (e) => {
        // Prevent drag from starting when mouse down occurs on button
        e.stopPropagation();
    };

    const handleButtonPointerDown = (e) => {
        // Prevent drag from starting when pointer down occurs on button
        e.stopPropagation();
    };

    const connectionClasses = [
        isConnectedToNext ? 'connected-to-next' : '',
        isConnectedFromPrevious ? 'connected-from-previous' : '',
        (isConnectedToNext || isConnectedFromPrevious) ? 'connected-rank' : ''
    ].filter(Boolean).join(' ');

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`vote-option-item ${sortableIsDragging ? 'dragging' : ''} ${isVetoed ? 'vetoed' : ''} ${connectionClasses}`}
            {...attributes}
            {...listeners}
        >
            <div className="vote-option-content">
                <div className="vote-option-rank">
                    {isVetoed ? '✗' : rank}
                </div>
                <div className="vote-option-text">{children}</div>
                <div className="vote-option-handle">
                    <button 
                        type="button"
                        className={isVetoed ? "unveto-button" : "veto-button"}
                        onClick={handleButtonClick}
                        onMouseDown={handleButtonMouseDown}
                        onPointerDown={handleButtonPointerDown}
                        title={isVetoed ? t('voting:actions.acceptOption') : t('voting:actions.vetoOption')}
                        aria-label={isVetoed ? t('voting:actions.acceptOption') : t('voting:actions.vetoOption')}
                    >
                        <span className={isVetoed ? "unveto-icon" : "veto-icon"}>
                            {isVetoed ? "✅" : "❌"}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function VetoEmptyState() {
    const { t } = useTranslation(['voting']);

    return (
        <div 
            className="vote-option-item veto-empty-state"
            style={{ 
                border: '2px dotted #ccc', 
                pointerEvents: 'none',
                minHeight: '80px',
                display: 'flex',
                alignItems: 'center'
            }}
        >
            <div className="vote-option-content" style={{ width: '100%' }}>
                <div className="vote-option-text" style={{ 
                    color: '#888', 
                    fontWeight: 'normal', 
                    textAlign: 'left',
                    flex: 1, 
                    paddingLeft: '15px',
                    paddingRight: '15px'
                }}>
                    {t('voting:cast.dragToVeto')}
                </div>
            </div>
        </div>
    );
}

function RankedEmptyState() {
    const { t } = useTranslation(['voting']);

    return (
        <div 
            className="vote-option-item ranked-empty-state"
            style={{ 
                border: '2px dotted #007bff', 
                pointerEvents: 'none',
                minHeight: '80px',
                display: 'flex',
                alignItems: 'center'
            }}
        >
            <div className="vote-option-content" style={{ width: '100%' }}>
                <div className="vote-option-text" style={{ 
                    color: '#888', 
                    fontWeight: 'normal', 
                    textAlign: 'left',
                    flex: 1, 
                    paddingLeft: '15px',
                    paddingRight: '15px'
                }}>
                    {t('voting:cast.dragToRank')}
                </div>
            </div>
        </div>
    );
}

function DroppableContainer({ id, items, children }) {
    const { setNodeRef } = useDroppable({ id });
    
    return (
        <div 
            ref={setNodeRef}
            className="sortable-container"
            style={{ minHeight: '120px', width: '100%', padding: '10px 0' }}
        >
            <SortableContext 
                items={items} 
                strategy={verticalListSortingStrategy}
            >
                {children}
            </SortableContext>
        </div>
    );
}


export default function VoteOptionsRenderer({
    options,
    onOptionsChange,
    onStartSort,
    isQuicksortActive,
    disabled = false,
    title,
    subtitle,
    enableVeto = false,
    showImagePreviews = false
}) {
    const { t } = useTranslation(['voting']);
    const dragInProgressRef = useRef(false);
    
    // Handle veto button click
    const handleVeto = useCallback((optionId) => {
        onOptionsChange(prevOptions => {
            const rankedOptions = [];
            const vetoedOptions = [];
            let newlyVetoedOption = null;
            let vetoedOptionIndex = -1;

            // Separate options and find the newly vetoed one
            prevOptions.forEach((option, index) => {
                if (option.id === optionId && !option.isVetoed) {
                    // Disconnect the vetoed item from any connections
                    newlyVetoedOption = { ...option, isVetoed: true, isConnectedToNext: false };
                    vetoedOptionIndex = index;
                } else if (option.isVetoed) {
                    vetoedOptions.push(option);
                } else {
                    rankedOptions.push(option);
                }
            });

            // Clean up connections where the vetoed item was removed
            let cleanedRankedOptions = rankedOptions;
            if (newlyVetoedOption && vetoedOptionIndex !== -1) {
                const itemBefore = prevOptions[vetoedOptionIndex - 1];
                const itemAfter = prevOptions[vetoedOptionIndex + 1];
                const vetoedItemWasConnected = prevOptions[vetoedOptionIndex].isConnectedToNext;

                // If there was an item before that was connected to the vetoed item,
                // and the vetoed item was connected to an item after, bridge the connection
                cleanedRankedOptions = rankedOptions.map(option => {
                    if (itemBefore && option.id === itemBefore.id && itemBefore.isConnectedToNext) {
                        const shouldConnectToNext = itemAfter && vetoedItemWasConnected &&
                            !itemAfter.isVetoed; // Only connect if the item after is not also vetoed
                        return { ...option, isConnectedToNext: shouldConnectToNext || false };
                    }
                    return option;
                });
            }

            // Put newly vetoed option at the top of veto list
            const finalVetoedOptions = newlyVetoedOption
                ? [newlyVetoedOption, ...vetoedOptions]
                : vetoedOptions;

            return [...cleanedRankedOptions, ...finalVetoedOptions];
        });
    }, [onOptionsChange]);

    // Handle un-veto button click
    const handleUnVeto = useCallback((optionId) => {
        onOptionsChange(prevOptions => {
            const rankedOptions = [];
            const vetoedOptions = [];
            let newlyUnVetoedOption = null;

            // Separate options and find the newly un-vetoed one
            prevOptions.forEach(option => {
                if (option.id === optionId && option.isVetoed) {
                    // Ensure the un-vetoed item remains disconnected
                    newlyUnVetoedOption = { ...option, isVetoed: false, isConnectedToNext: false };
                } else if (option.isVetoed) {
                    vetoedOptions.push(option);
                } else {
                    rankedOptions.push(option);
                }
            });

            // Put newly un-vetoed option at the end of ranked list (disconnected)
            const finalRankedOptions = newlyUnVetoedOption
                ? [...rankedOptions, newlyUnVetoedOption]
                : rankedOptions;

            return [...finalRankedOptions, ...vetoedOptions];
        });
    }, [onOptionsChange]);

    // Handle connecting/disconnecting ranks
    const handleToggleConnection = useCallback((upperOptionId, lowerOptionId, shouldConnect) => {
        onOptionsChange(prevOptions => {
            return prevOptions.map(option => {
                if (option.id === upperOptionId) {
                    return { ...option, isConnectedToNext: shouldConnect };
                }
                return option;
            });
        });
    }, [onOptionsChange]);

    // Calculate ranks for options considering connections (sports-style ranking)
    const calculateOptionRank = useCallback((option, index, rankedOptions) => {
        // If this option is connected to the previous one, use the previous option's rank
        if (index > 0 && rankedOptions[index - 1].isConnectedToNext) {
            return calculateOptionRank(rankedOptions[index - 1], index - 1, rankedOptions);
        }
        // Sports-style ranking: count actual positions, not just rank groups
        // Each item takes up a position, so the rank is index + 1
        return index + 1;
    }, []);

    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Separate options into containers - this is the key pattern from dnd-kit examples
    const containers = {
        'ranked': options.filter(opt => !opt.isVetoed),
        'vetoed': options.filter(opt => opt.isVetoed)
    };
    
    // Helper function to get connected group for an option
    const getConnectedGroup = useCallback((options, targetId) => {
        const targetIndex = options.findIndex(opt => opt.id === targetId);
        if (targetIndex === -1) return [];

        const group = [options[targetIndex]];

        // Look backward for connected options
        for (let i = targetIndex - 1; i >= 0; i--) {
            if (options[i].isConnectedToNext) {
                group.unshift(options[i]);
            } else {
                break;
            }
        }

        // Look forward for connected options
        for (let i = targetIndex; i < options.length - 1; i++) {
            if (options[i].isConnectedToNext) {
                group.push(options[i + 1]);
            } else {
                break;
            }
        }

        return group;
    }, []);


    // Helper function to detect if insertion is between connected items
    const isInsertionBetweenConnectedItems = useCallback((options, targetIndex) => {
        if (targetIndex < 0 || targetIndex >= options.length) {
            return false;
        }

        const targetItem = options[targetIndex];
        const prevItem = options[targetIndex - 1];

        console.log('🔍 isInsertionBetweenConnectedItems DEBUG:');
        console.log('   - targetIndex:', targetIndex);
        console.log('   - targetItem:', targetItem?.id, 'isConnectedToNext:', targetItem?.isConnectedToNext);
        console.log('   - prevItem:', prevItem?.id, 'isConnectedToNext:', prevItem?.isConnectedToNext);

        // We're inserting "between connected items" in two cases:
        // 1. The previous item is connected to the target (inserting into middle of group)
        // 2. The target item is connected to the next item (inserting before a connected pair)

        const prevConnectedToTarget = prevItem?.isConnectedToNext === true;
        const targetConnectedToNext = targetItem?.isConnectedToNext === true;

        const result = prevConnectedToTarget || targetConnectedToNext;
        console.log('   - prevConnectedToTarget:', prevConnectedToTarget);
        console.log('   - targetConnectedToNext:', targetConnectedToNext);
        console.log('   - result:', result);

        return result;
    }, []);

    // Helper function to clean up connections when an item is removed from a group
    const cleanupConnectionsAfterRemoval = useCallback((optionsWithoutRemoved, removedIndex, removedItemId, originalOptions) => {
        // Find what was before and after the removed item in the original array
        const itemBefore = originalOptions[removedIndex - 1];
        const itemAfter = originalOptions[removedIndex + 1];

        return optionsWithoutRemoved.map(option => {
            // If the item before the removed item was connected to the removed item,
            // it should now connect to what comes after the removed item (if the removed item was also connected)
            if (itemBefore && option.id === itemBefore.id && itemBefore.isConnectedToNext) {
                // The item before should connect to the item after only if the removed item was also connected forward
                const removedItemWasConnected = originalOptions[removedIndex].isConnectedToNext;
                const shouldConnectToNext = itemAfter && removedItemWasConnected;
                return { ...option, isConnectedToNext: shouldConnectToNext || false };
            }

            return option;
        });
    }, []);

    // Helper function to update connections after inserting item into group
    const updateConnectionsAfterInsertion = useCallback((options, insertedItemId, insertIndex) => {
        // Check if we're inserting into a connected group by looking at the connection FROM the item before insertion
        const itemBeforeInsertion = options[insertIndex - 1];
        const itemAfterInsertion = options[insertIndex + 1];
        const wasInsertedIntoConnectedGroup = itemBeforeInsertion &&
            itemBeforeInsertion.isConnectedToNext === true;

        console.log('🔗 updateConnectionsAfterInsertion DEBUG:');
        console.log('   - insertedItemId:', insertedItemId);
        console.log('   - insertIndex:', insertIndex);
        console.log('   - itemBeforeInsertion:', itemBeforeInsertion?.id, 'isConnectedToNext:', itemBeforeInsertion?.isConnectedToNext);
        console.log('   - itemAfterInsertion:', itemAfterInsertion?.id, 'isConnectedToNext:', itemAfterInsertion?.isConnectedToNext);
        console.log('   - wasInsertedIntoConnectedGroup:', wasInsertedIntoConnectedGroup);
        console.log('   - options before:', options.map(o => ({ id: o.id, isConnectedToNext: o.isConnectedToNext })));

        const result = options.map((option, index) => {
            if (option.id === insertedItemId) {
                // Inserted item should connect to next item if we inserted into a connected group
                // AND if the next item exists
                const hasNext = index < options.length - 1;
                const shouldConnect = hasNext && wasInsertedIntoConnectedGroup;
                console.log('   - inserted item shouldConnect:', shouldConnect, '(hasNext:', hasNext, ')');
                return { ...option, isConnectedToNext: shouldConnect };
            }

            // Item before insertion: if we inserted into a connected group, it should stay connected
            if (index === insertIndex - 1) {
                if (wasInsertedIntoConnectedGroup) {
                    // We're inserting into a connected group - item before stays connected to the newly inserted item
                    console.log('   - item before insertion stays connected');
                    return { ...option, isConnectedToNext: true };
                } else {
                    // Not inserting into connected group - disconnect
                    console.log('   - item before insertion gets disconnected');
                    return { ...option, isConnectedToNext: false };
                }
            }

            // Item after insertion: if we inserted into a connected group, preserve its connection state
            // But we need to be careful - if the item after was originally connected to what comes after it,
            // it should remain connected. If it wasn't, it shouldn't be.
            if (index === insertIndex + 1) {
                console.log('   - item after insertion preserves connection:', option.isConnectedToNext);
                return option;
            }

            return option;
        });

        console.log('   - options after:', result.map(o => ({ id: o.id, isConnectedToNext: o.isConnectedToNext })));
        return result;
    }, []);


    const handleDragOver = useCallback((event) => {
        const { active, over } = event;

        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        // Use working template approach - determine containers inside state update
        onOptionsChange(prev => {
            // Find containers using current state, not stale closure
            const findContainerInState = (id, opts) => {
                if (id === 'ranked-container') return 'ranked';
                if (id === 'vetoed-container') return 'vetoed';
                if (opts.find(item => item.id === id && !item.isVetoed)) return 'ranked';
                if (opts.find(item => item.id === id && item.isVetoed)) return 'vetoed';
                return null;
            };

            const activeContainer = findContainerInState(activeId, prev);
            const overContainer = findContainerInState(overId, prev);

            if (!activeContainer || !overContainer || activeContainer === overContainer) {
                return prev;
            }

            const overItems = prev.filter(opt =>
                overContainer === 'ranked' ? !opt.isVetoed : opt.isVetoed
            );

            // For cross-container moves, only move the single item, not the entire connected group
            const activeItem = prev.find(item => item.id === activeId);
            if (!activeItem) {
                return prev;
            }

            // Calculate new position - now allows insertion anywhere including mid-group
            let newIndex;
            if (overId === 'ranked-container' || overId === 'vetoed-container') {
                newIndex = overItems.length;
            } else {
                // Allow insertion at any position, including within connected groups
                const overIndex = overItems.findIndex(item => item.id === overId);
                newIndex = overIndex >= 0 ? overIndex : overItems.length;
            }

            // Remove only the single active item from source container and clean up connections
            let remainingItems = prev.filter(item => item.id !== activeId);

            // If the item was part of a connected group, clean up the connections
            if (activeContainer === 'ranked') {
                const sourceItems = prev.filter(opt => !opt.isVetoed);
                const activeIndex = sourceItems.findIndex(item => item.id === activeId);
                if (activeIndex !== -1) {
                    const sourceGroup = getConnectedGroup(sourceItems, activeId);
                    if (sourceGroup.length > 1) {
                        // Item was part of a group, clean up connections
                        const rankedItems = remainingItems.filter(opt => !opt.isVetoed);
                        const cleanedRanked = cleanupConnectionsAfterRemoval(rankedItems, activeIndex, activeId, sourceItems);
                        remainingItems = [...cleanedRanked, ...remainingItems.filter(opt => opt.isVetoed)];
                    }
                }
            }

            // Update the single item for the target container
            const updatedItem = {
                ...activeItem,
                isVetoed: overContainer === 'vetoed',
                isConnectedToNext: false // Disconnect when moving between containers
            };

            const targetItems = remainingItems.filter(item =>
                overContainer === 'ranked' ? !item.isVetoed : item.isVetoed
            );
            const newTargetItems = [
                ...targetItems.slice(0, newIndex),
                updatedItem,
                ...targetItems.slice(newIndex)
            ];

            // Reconstruct full array
            if (overContainer === 'ranked') {
                return [...newTargetItems, ...remainingItems.filter(item => item.isVetoed)];
            } else {
                return [...remainingItems.filter(item => !item.isVetoed), ...newTargetItems];
            }
        });
    }, [onOptionsChange, getConnectedGroup, cleanupConnectionsAfterRemoval]);

    const handleDragStart = useCallback((event) => {
        dragInProgressRef.current = true;
    }, []);

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;

        dragInProgressRef.current = false;

        if (!over) {
            return;
        }

        const activeId = active.id;
        const overId = over.id;

        onOptionsChange(prevOptions => {
            // Find containers using current state
            const findContainerInState = (id, opts) => {
                if (id === 'ranked-container') return 'ranked';
                if (id === 'vetoed-container') return 'vetoed';
                if (opts.find(item => item.id === id && !item.isVetoed)) return 'ranked';
                if (opts.find(item => item.id === id && item.isVetoed)) return 'vetoed';
                return null;
            };

            const activeContainer = findContainerInState(activeId, prevOptions);
            const overContainer = findContainerInState(overId, prevOptions);

            // Only handle same-container reordering in dragEnd
            if (!activeContainer || !overContainer || activeContainer !== overContainer) {
                return prevOptions;
            }

            // Skip if dropping on container itself (not reordering)
            if (overId === 'ranked-container' || overId === 'vetoed-container') {
                return prevOptions;
            }

            const containerItems = activeContainer === 'ranked'
                ? prevOptions.filter(opt => !opt.isVetoed)
                : prevOptions.filter(opt => opt.isVetoed);

            // Get connected group for the active item
            const connectedGroup = getConnectedGroup(containerItems, activeId);

            if (connectedGroup.length === 0) {
                return prevOptions;
            }

            const activeIndex = containerItems.findIndex(item => item.id === activeId);
            const overIndex = containerItems.findIndex(item => item.id === overId);

            if (activeIndex !== -1 && overIndex !== -1) {
                console.log('🔥 handleDragEnd DEBUG:');
                console.log('   - activeId:', activeId, 'overId:', overId);
                console.log('   - activeIndex:', activeIndex, 'overIndex:', overIndex);
                console.log('   - containerItems:', containerItems.map(item => ({ id: item.id, isConnectedToNext: item.isConnectedToNext })));

                // Analyze the move scenario BEFORE removing the dragged item
                // Determine what type of move this is
                const sourceGroup = getConnectedGroup(containerItems, activeId);
                const isMovingFromGroup = sourceGroup.length > 1; // Item is part of a connected group

                // Check if target location is within a connected group
                const isInsertingBetweenConnected = isInsertionBetweenConnectedItems(containerItems, overIndex);

                console.log('   - sourceGroup:', sourceGroup.map(item => item.id));
                console.log('   - isMovingFromGroup:', isMovingFromGroup);
                console.log('   - isInsertingBetweenConnected:', isInsertingBetweenConnected);

                // Check if this is reordering within the same connected group
                const targetGroup = getConnectedGroup(containerItems, overId);
                const isReorderingWithinSameGroup = sourceGroup.length > 1 &&
                    targetGroup.some(item => sourceGroup.some(sourceItem => sourceItem.id === item.id));

                // Step 1: Remove the moved item and clean up its source connections
                let cleanedOptions = containerItems.filter(item => item.id !== activeId);

                // If moving from a group, clean up the broken connections
                if (isMovingFromGroup) {
                    cleanedOptions = cleanupConnectionsAfterRemoval(cleanedOptions, activeIndex, activeId, containerItems);
                }

                // Step 2: Determine insertion position
                let adjustedInsertIndex;

                if (isInsertingBetweenConnected) {
                    // Determine which type of connected insertion this is
                    const targetConnectedToNext = containerItems[overIndex]?.isConnectedToNext === true;
                    const prevConnectedToTarget = containerItems[overIndex - 1]?.isConnectedToNext === true;

                    if (prevConnectedToTarget && !targetConnectedToNext) {
                        // Case: A→B, inserting C on B -> A,C,B (replace B's position)
                        adjustedInsertIndex = overIndex;
                    } else if (targetConnectedToNext) {
                        // Case: B→C, inserting A on B -> B,A,C (after B)
                        adjustedInsertIndex = overIndex + 1;
                    } else {
                        // Fallback: use target position
                        adjustedInsertIndex = overIndex;
                    }
                } else {
                    // Normal insertion: insert at the target position
                    adjustedInsertIndex = overIndex;
                }

                // Find where the target item will be in the cleaned array
                const targetItemId = containerItems[overIndex]?.id;
                const targetIndexInCleaned = cleanedOptions.findIndex(item => item.id === targetItemId);

                if (targetIndexInCleaned !== -1) {
                    // The key insight: we want to insert at the position where we dropped
                    // If we're moving DOWN (activeIndex < overIndex), insert after target
                    // If we're moving UP (activeIndex > overIndex), insert before target
                    if (activeIndex < overIndex) {
                        // Moving down: insert after the target item
                        adjustedInsertIndex = targetIndexInCleaned + 1;
                    } else {
                        // Moving up: insert before the target item
                        adjustedInsertIndex = targetIndexInCleaned;
                    }
                } else {
                    // Fallback: use simple adjustment
                    if (activeIndex < adjustedInsertIndex) {
                        adjustedInsertIndex--;
                    }
                }

                // Ensure insertion index is within bounds
                adjustedInsertIndex = Math.max(0, Math.min(adjustedInsertIndex, cleanedOptions.length));

                // Step 3: Insert the moved item (just the single item, not the whole group)
                const movedItem = containerItems.find(item => item.id === activeId);
                let reordered = [
                    ...cleanedOptions.slice(0, adjustedInsertIndex),
                    movedItem,
                    ...cleanedOptions.slice(adjustedInsertIndex)
                ];

                // Step 4: Update connections based on move type
                console.log('   - isReorderingWithinSameGroup:', isReorderingWithinSameGroup);
                console.log('   - targetGroup:', targetGroup.map(item => item.id));

                if (isReorderingWithinSameGroup) {
                    console.log('   - BRANCH: isReorderingWithinSameGroup');
                    // For intra-group moves, we need to rebuild the group connections properly
                    const wholeGroup = targetGroup; // Use target group since it's the same as source group
                    const groupStartIndex = reordered.findIndex(item =>
                        wholeGroup.some(groupItem => groupItem.id === item.id)
                    );

                    // Update connections within the group: all items except the last should be connected
                    for (let i = groupStartIndex; i < groupStartIndex + wholeGroup.length; i++) {
                        if (i < reordered.length) {
                            const isLastInGroup = i === groupStartIndex + wholeGroup.length - 1;
                            reordered[i] = { ...reordered[i], isConnectedToNext: !isLastInGroup };
                        }
                    }
                } else if (isInsertingBetweenConnected) {
                    console.log('   - BRANCH: isInsertingBetweenConnected');
                    const insertedItemId = movedItem.id;

                    // Update connections to integrate the item into the group
                    reordered = updateConnectionsAfterInsertion(reordered, insertedItemId, adjustedInsertIndex);
                } else if (isMovingFromGroup && !isInsertingBetweenConnected) {
                    console.log('   - BRANCH: isMovingFromGroup && !isInsertingBetweenConnected');
                    // Item moved out of group to a position that's not connected, ensure it's disconnected
                    reordered = reordered.map(item =>
                        item.id === activeId
                            ? { ...item, isConnectedToNext: false }
                            : item
                    );
                } else {
                    console.log('   - BRANCH: else (no connection updates)');
                }

                // Reconstruct full options array
                const finalResult = activeContainer === 'ranked'
                    ? [...reordered, ...prevOptions.filter(opt => opt.isVetoed)]
                    : [...prevOptions.filter(opt => !opt.isVetoed), ...reordered];

                return finalResult;
            }

            return prevOptions;
        });
    }, [onOptionsChange, getConnectedGroup, isInsertionBetweenConnectedItems, updateConnectionsAfterInsertion, cleanupConnectionsAfterRemoval]);

    // No need for combined items list - each SortableContext manages its own items

    return (
        <div className="vote-creation-card mb-4">
            <div className="voting-header">
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <h3>{title || t('voting:cast.subtitle')}</h3>
                        <p className="text-muted mb-0">{subtitle || t('voting:cast.instructions')}</p>
                    </div>
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={onStartSort}
                        disabled={disabled || containers['ranked'].length <= 1 || isQuicksortActive}
                    >
                        {t('voting:cast.sortInteractively')}
                    </button>
                </div>
            </div>
            
            {options.length === 0 ? (
                <div className="text-center py-4 text-muted">
                    {t('voting:states.noOptions')}
                </div>
            ) : (
                <div className="voting-interface">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCorners}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDragEnd={handleDragEnd}
                    >
                        {/* Ranked Options Section */}
                        <div className="ranked-options-section">
                            <h4 className="section-title">{t('voting:cast.rankedOptions')}</h4>
                            <DroppableContainer 
                                id="ranked-container" 
                                items={containers['ranked'].map(opt => opt.id)}
                            >
                                <div className="vote-options-list">
                                    {containers['ranked'].length === 0 ? (
                                        <RankedEmptyState />
                                    ) : (
                                        containers['ranked'].flatMap((option, index) => {
                                            const currentRank = calculateOptionRank(option, index, containers['ranked']);
                                            const isConnectedFromPrevious = index > 0 && containers['ranked'][index - 1].isConnectedToNext;

                                            const elements = [
                                                <SortableItem
                                                    key={option.id}
                                                    id={option.id}
                                                    rank={currentRank}
                                                    isVetoed={false}
                                                    onVeto={handleVeto}
                                                    onUnVeto={handleUnVeto}
                                                    isConnectedToNext={option.isConnectedToNext}
                                                    isConnectedFromPrevious={isConnectedFromPrevious}
                                                    t={t}
                                                >
                                                    <LinkText text={option.name} showImagePreviews={showImagePreviews} />
                                                </SortableItem>
                                            ];

                                            // Add ConnectRanksButton after each item (except the last one)
                                            if (index < containers['ranked'].length - 1) {
                                                const nextOption = containers['ranked'][index + 1];
                                                const containerClass = option.isConnectedToNext
                                                    ? "connect-ranks-container connected-items"
                                                    : "connect-ranks-container";
                                                elements.push(
                                                    <div key={`connect-${option.id}-${nextOption.id}`} className={containerClass}>
                                                        <ConnectRanksButton
                                                            isConnected={option.isConnectedToNext}
                                                            onToggleConnection={handleToggleConnection}
                                                            upperOptionId={option.id}
                                                            lowerOptionId={nextOption.id}
                                                            disabled={disabled}
                                                        />
                                                    </div>
                                                );
                                            }

                                            return elements;
                                        })
                                    )}
                                </div>
                            </DroppableContainer>
                        </div>

                        {/* Veto Section */}
                        {enableVeto && (
                            <div className="veto-section mt-5">
                                <h4 className="section-title text-muted">{t('voting:cast.vetoedOptions')}</h4>
                                <DroppableContainer 
                                    id="vetoed-container" 
                                    items={containers['vetoed'].map(opt => opt.id)}
                                >
                                    <div className="vote-options-list vetoed-list">
                                        {containers['vetoed'].length === 0 ? (
                                            <VetoEmptyState />
                                        ) : (
                                            containers['vetoed'].map((option, index) => (
                                                <SortableItem 
                                                    key={option.id} 
                                                    id={option.id}
                                                    rank={containers['ranked'].length + index + 1}
                                                    isVetoed={true}
                                                    onVeto={handleVeto}
                                                    onUnVeto={handleUnVeto}
                                                    t={t}
                                                >
                                                    <LinkText text={option.name} showImagePreviews={showImagePreviews} />
                                                </SortableItem>
                                            ))
                                        )}
                                    </div>
                                </DroppableContainer>
                            </div>
                        )}
                    </DndContext>
                </div>
            )}
        </div>
    );
}